/**
 * Regulation browser — Legal.Regulation with level, bindingForce and
 * coverageStatus; drill-down into Provisions (full text) and the
 * Obligations each establishes. Coverage stubs (state law, DIN) appear
 * here too, clearly labeled as not covered.
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, Ruler, Search } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { useUrlPageState } from "../../utilities/dataTablePagination";
import {
  ReviewStatusBadge,
  CoverageStatusBadge,
  ConfidenceIndicator,
  WrappedText,
  formatDate,
} from "./legalDisplay";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Properties {
  regulations: any[];
  refreshSignal: number;
  onOpenObligation: (obligationKey: string) => void;
}

/** Where the search term lives, next to the page — so a reload or "back" keeps both. */
const SEARCH_PARAM = "legalRegulationsSearch";
const PAGE_PARAM = "legalRegulationsPage";

/**
 * Words that name a level rather than a piece of text: "land" means state law, not every title
 * with "Land" in it.
 *
 * As text they would not work. State law is identified by its state (`de/nw/…`, `de/by/…`), so
 * "land" found none of it, and "eu" matched every "neu", "Steuer" and "Feuer" in a title. Federal
 * law happened to match through `de/bund/…`, which made the other two look broken.
 */
const LEVEL_WORDS: Record<string, string> = {
  eu: "EU",
  bund: "Federal",
  federal: "Federal",
  land: "State",
  state: "State",
};

/**
 * Whether a regulation matches what was typed.
 *
 * Every word has to hold — "BetrSichV Prüfung" narrows, it does not widen. A level word (EU, Bund,
 * Land) asks for that level; any other word has to occur in the short name, the title or the ELI
 * id. Case does not matter.
 */
export const matchesRegulationSearch = (regulation: any, query: string): boolean => {
  const words = query.toLocaleLowerCase("de").split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;

  const haystack = [regulation.shortName, regulation.title, regulation.eliId]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("de");
  return words.every((word) =>
    LEVEL_WORDS[word] ? regulation.level === LEVEL_WORDS[word] : haystack.includes(word),
  );
};

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

export default function RegulationBrowserPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const regulationsPagination = useUrlPageState(PAGE_PARAM);
  const provisionsPagination = useUrlPageState("legalProvisionsPage");
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(SEARCH_PARAM) ?? "";
  const searchInputId = `${useId()}-regulation-search`;

  const [selectedRegulation, setSelectedRegulation] = useState<any>(null);
  const [provisions, setProvisions] = useState<any[]>([]);
  const [selectedProvision, setSelectedProvision] = useState<any>(null);
  const [obligations, setObligations] = useState<any[]>([]);

  useEffect(() => {
    setSelectedRegulation(null);
    setSelectedProvision(null);
  }, [properties.refreshSignal]);

  const openRegulation = async (regulation: any) => {
    // Clear child lists up front so a failed load (or slow response) never
    // shows the previous regulation's provisions under the new header.
    setSelectedRegulation(regulation);
    setSelectedProvision(null);
    setProvisions([]);
    setObligations([]);

    try {
      setProvisions(await legalAPI.getProvisions(regulation.eliId));
    } catch (error) {
      pushError(error);
    }
  };

  const openProvision = async (provision: any) => {
    setSelectedProvision(provision);
    setObligations([]);

    try {
      const result = await legalAPI.getObligations({
        regulationId: selectedRegulation.eliId,
        provisionId: provision.provisionId,
        limit: 200,
      });

      setObligations(result.items || []);
    } catch (error) {
      pushError(error);
    }
  };

  /**
   * A new term starts at the first page. Staying on page 4 of a list that now has one page would
   * show nothing and look like "no match". One write for both, from the current address — the
   * same reason `useUrlPageState` builds from `current`.
   */
  const changeSearch = (value: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(SEARCH_PARAM, value);
        else next.delete(SEARCH_PARAM);
        next.set(PAGE_PARAM, "1");
        return next;
      },
      { replace: true },
    );

  const shownRegulations = useMemo(
    () =>
      (properties.regulations || []).filter((regulation: any) =>
        matchesRegulationSearch(regulation, search),
      ),
    [properties.regulations, search],
  );

  // bindingForce separates the two kinds: 'binding' = law/ordinance,
  // recognizedRuleOfTechnology / stateOfTheArt = technical norm (DIN/VDI).
  const isNorm = (regulation: any) => regulation.bindingForce !== "binding";

  const regulationColumns: ColumnDef<any>[] = [
    {
      id: "shortName",
      accessorFn: (regulation: any) => regulation.shortName || regulation.eliId,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.shortName")}</SortableHeader>
      ),
      size: 180,
      cell: ({ row }) => (
        <span className="flex min-w-0 items-center gap-1.5">
          {isNorm(row.original) ? (
            <Ruler
              className="h-4 w-4 shrink-0 text-primary"
              aria-label={t("operatorResponsibilityPanel.regulationKind.norm")}
            >
              <title>{t("operatorResponsibilityPanel.regulationKind.norm")}</title>
            </Ruler>
          ) : (
            // The section sign is the canonical symbol for German statutes;
            // lucide has no equivalent icon, so render the character itself.
            <span
              title={t("operatorResponsibilityPanel.regulationKind.law")}
              aria-label={t("operatorResponsibilityPanel.regulationKind.law")}
              className="w-4 shrink-0 text-center text-sm font-bold leading-none text-primary"
            >
              §
            </span>
          )}
          <span className="min-w-0 break-words">
            {row.original.shortName || row.original.eliId}
          </span>
        </span>
      ),
    },
    {
      id: "title",
      accessorFn: (regulation: any) => regulation.title,
      header: t("operatorResponsibilityPanel.regulationTitle"),
      size: 280,
      enableSorting: false,
      cell: ({ row }) => <WrappedText text={row.original.title} maxWidth={280} fontSize={12} />,
    },
    {
      id: "level",
      accessorFn: (regulation: any) =>
        t(`operatorResponsibilityPanel.levelValue.${regulation.level || "unknown"}`),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.level")}</SortableHeader>
      ),
      size: 100,
    },
    {
      id: "bindingForce",
      accessorFn: (regulation: any) =>
        t(`operatorResponsibilityPanel.bindingForce.${regulation.bindingForce}`),
      header: t("operatorResponsibilityPanel.bindingForce.label"),
      size: 200,
      enableSorting: false,
    },
    {
      id: "coverage",
      accessorFn: (regulation: any) => regulation.coverageStatus,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.coverage")}</SortableHeader>
      ),
      size: 140,
      cell: ({ row }) => <CoverageStatusBadge status={row.original.coverageStatus} />,
    },
    {
      id: "provisions",
      accessorFn: (regulation: any) => regulation.provisionCount,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.provisions")}</SortableHeader>
      ),
      size: 110,
    },
    {
      id: "obligations",
      accessorFn: (regulation: any) => regulation.obligationCount,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.obligations")}</SortableHeader>
      ),
      size: 170,
      cell: ({ row }) => (
        <span className="min-w-0 break-words">
          {row.original.obligationCount}
          {row.original.pendingObligationCount > 0 ? (
            <span className="font-semibold text-warning">
              {" "}
              ({row.original.pendingObligationCount}{" "}
              {t("operatorResponsibilityPanel.reviewStatus.pending")})
            </span>
          ) : null}
        </span>
      ),
    },
  ];

  const provisionColumns: ColumnDef<any>[] = [
    {
      id: "provisionId",
      accessorFn: (provision: any) => provision.provisionId,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.provision")}</SortableHeader>
      ),
      size: 120,
    },
    {
      id: "citation",
      accessorFn: (provision: any) => provision.citation,
      header: t("operatorResponsibilityPanel.citation"),
      size: 320,
      enableSorting: false,
    },
    {
      id: "text",
      header: t("operatorResponsibilityPanel.textPreview"),
      enableSorting: false,
      cell: ({ row }) => <WrappedText text={row.original.text} maxWidth={420} />,
    },
    {
      id: "obligationCount",
      accessorFn: (provision: any) => provision.obligationCount,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.obligations")}</SortableHeader>
      ),
      size: 110,
    },
  ];

  if (!selectedRegulation) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex min-w-0 flex-wrap items-end gap-4">
          <div className="flex w-[320px] max-w-full flex-col gap-1.5">
            <Label htmlFor={searchInputId}>
              {t("operatorResponsibilityPanel.regulationSearch.label")}
            </Label>
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id={searchInputId}
                type="search"
                className="pl-9"
                placeholder={t("operatorResponsibilityPanel.regulationSearch.placeholder")}
                value={search}
                onChange={(event) => changeSearch(event.target.value)}
              />
            </div>
          </div>
          {search ? (
            <p className="text-sm text-muted-foreground max-sm:w-full sm:pb-2.5">
              {t("operatorResponsibilityPanel.regulationSearch.countShown", {
                shown: shownRegulations.length,
                total: (properties.regulations || []).length,
              })}
            </p>
          ) : null}
        </div>

        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={regulationColumns}
            data={shownRegulations}
            pageSize={5}
            page={regulationsPagination.page}
            onPageChange={regulationsPagination.onPageChange}
            onRowClick={(regulation: any) => openRegulation(regulation)}
            previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
            nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
            emptyMessage={
              search && (properties.regulations || []).length > 0 ? (
                <span className="flex min-w-0 flex-wrap items-center justify-center gap-2 break-words">
                  {t("operatorResponsibilityPanel.regulationSearch.noMatch", { search })}
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => changeSearch("")}
                  >
                    {t("operatorResponsibilityPanel.regulationSearch.reset")}
                  </Button>
                </span>
              ) : (
                <span className="min-w-0 break-words">
                  {t("operatorResponsibilityPanel.noRegulations")}
                </span>
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <h3 className="min-w-0 break-words text-lg font-semibold">
            {selectedRegulation.shortName || selectedRegulation.eliId}
          </h3>
          <CoverageStatusBadge status={selectedRegulation.coverageStatus} />
        </div>
        <Button
          variant="outline"
          className="gap-2 max-sm:w-full"
          onClick={() => {
            if (selectedProvision) {
              setSelectedProvision(null);
            } else {
              setSelectedRegulation(null);
            }
          }}
        >
          <ArrowLeft aria-hidden className="h-4 w-4" />
          {t("global.back")}
        </Button>
      </div>

      <p className="min-w-0 break-words text-sm text-muted-foreground">
        {selectedRegulation.title} · {selectedRegulation.eliId} ·{" "}
        {t(`operatorResponsibilityPanel.bindingForce.${selectedRegulation.bindingForce}`)} ·{" "}
        {t("operatorResponsibilityPanel.asOf", { date: formatDate(selectedRegulation.asOfDate) })}
      </p>

      {selectedRegulation.coverageStatus !== "covered" ? (
        <Alert variant="destructive">
          <AlertDescription className="min-w-0 break-words">
            {t("operatorResponsibilityPanel.notCoveredNote")}
            {selectedRegulation.coverageNote ? ` — ${selectedRegulation.coverageNote}` : ""}
          </AlertDescription>
        </Alert>
      ) : null}

      {!selectedProvision ? (
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={provisionColumns}
            data={provisions}
            pageSize={5}
            page={provisionsPagination.page}
            onPageChange={provisionsPagination.onPageChange}
            onRowClick={(provision: any) => openProvision(provision)}
            previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
            nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
            emptyMessage={
              <span className="min-w-0 break-words">
                {t("operatorResponsibilityPanel.noProvisions")}
              </span>
            }
          />
        </div>
      ) : (
        // Wortlaut links, abgeleitete Pflichten rechts — bei schmalem Viewport
        // untereinander, damit der Wortlaut nicht auf 40 Zeichen schrumpft.
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-6">
          <Section title={selectedProvision.citation}>
            <div className="max-h-[520px] min-w-0 overflow-y-auto whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
              {selectedProvision.text}
            </div>
          </Section>
          <Section
            title={t("operatorResponsibilityPanel.establishedObligations", {
              count: obligations.length,
            })}
          >
            {obligations.length === 0 ? (
              <p className="min-w-0 break-words text-sm text-muted-foreground">
                {t("operatorResponsibilityPanel.noObligationsForProvision")}
              </p>
            ) : (
              <div className="flex min-w-0 flex-col">
                {obligations.map((obligation: any) => (
                  <button
                    key={`${obligation.obligationKey}@${obligation.version}`}
                    type="button"
                    className="flex min-w-0 flex-col gap-1 border-b py-3 text-left hover:bg-accent"
                    onClick={() => properties.onOpenObligation(obligation.obligationKey)}
                  >
                    <span className="flex min-w-0 flex-wrap items-center gap-3">
                      <span className="font-semibold">{obligation.obligationKey}</span>
                      <ReviewStatusBadge status={obligation.reviewStatus} />
                      <ConfidenceIndicator value={obligation.confidence} />
                    </span>
                    <span className="min-w-0 break-words text-sm">{obligation.description}</span>
                  </button>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </div>
  );
}
